import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Chips } from 'primereact/chips';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { FormikProps } from 'formik';

import {
    ColumnDto,
    ColumnDtoTypeEnum,
    DataSourceDto,
    FilterDto,
    FilterDtoOperatorEnum,
    FiltersDtoOperatorEnum,
} from '../../../../../../../typescript-axios';
import { getAvailableFilterOperators } from '../../../utils/datasource_utils';
import { MarsinetContext } from '../../../../../../../context/marsinetProvider';
import {
    getLocalDateTimeInTimeZone,
    localDateTimeToUtc,
} from '../../../../../../../utils/timezone-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StepFiltersProps {
    formik: FormikProps<{ dataSource: DataSourceDto }>;
    filtersPath: string;
    columnOptions: Record<string, ColumnDto>;
}

// ─── Operator catalogue per column type ───────────────────────────────────────

const OPERATOR_SHORT: Partial<Record<FilterDtoOperatorEnum, string>> = {
    [FilterDtoOperatorEnum.Eq]: '=',
    [FilterDtoOperatorEnum.Neq]: '≠',
    [FilterDtoOperatorEnum.Gt]: '>',
    [FilterDtoOperatorEnum.Gte]: '≥',
    [FilterDtoOperatorEnum.Lt]: '<',
    [FilterDtoOperatorEnum.Lte]: '≤',
    [FilterDtoOperatorEnum.Between]: 'between',
    [FilterDtoOperatorEnum.NotBetween]: 'not between',
    [FilterDtoOperatorEnum.In]: 'in',
    [FilterDtoOperatorEnum.NotIn]: 'not in',
    [FilterDtoOperatorEnum.Like]: 'like',
    [FilterDtoOperatorEnum.NotLike]: 'not like',
    [FilterDtoOperatorEnum.Ilike]: 'ilike',
    [FilterDtoOperatorEnum.NotIlike]: 'not ilike',
    [FilterDtoOperatorEnum.Regexp]: 'regexp',
    [FilterDtoOperatorEnum.NotRegexp]: 'not regexp',
    [FilterDtoOperatorEnum.IsNull]: 'is null',
    [FilterDtoOperatorEnum.IsNotNull]: 'is not null',
};

// ─── Operator shape helpers ────────────────────────────────────────────────────

const NO_VALUE_OPERATORS = new Set<FilterDtoOperatorEnum>([
    FilterDtoOperatorEnum.IsNull,
    FilterDtoOperatorEnum.IsNotNull,
]);

const ARRAY_VALUE_OPERATORS = new Set<FilterDtoOperatorEnum>([
    FilterDtoOperatorEnum.In,
    FilterDtoOperatorEnum.NotIn,
]);

const RANGE_OPERATORS = new Set<FilterDtoOperatorEnum>([
    FilterDtoOperatorEnum.Between,
    FilterDtoOperatorEnum.NotBetween,
]);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function defaultValueForOperator(
    op: FilterDtoOperatorEnum,
    colType: ColumnDtoTypeEnum
): FilterDto['value'] {
    if (NO_VALUE_OPERATORS.has(op)) return undefined;
    if (ARRAY_VALUE_OPERATORS.has(op)) return [];
    if (RANGE_OPERATORS.has(op)) return colType === ColumnDtoTypeEnum.Number ? [null, null] : ['', ''];
    return colType === ColumnDtoTypeEnum.Number ? null : '';
}

// ─── Shared label style ───────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-color-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
};

// ─── TimestampInput ───────────────────────────────────────────────────────────

const TimestampInput: React.FC<{
    value: string | null | undefined;
    onChange: (v: string) => void;
    timeZone: string;
}> = ({ value, onChange, timeZone }) => {
    const [mode, setMode] = useState<'absolute' | 'relative'>(() =>
        typeof value === 'string' && value.startsWith('now') ? 'relative' : 'absolute'
    );
    const [conversionError, setConversionError] = useState<string | null>(null);

    const RELATIVE_UNITS = [
        { label: 'seconds', value: 's' },
        { label: 'minutes', value: 'm' },
        { label: 'hours', value: 'h' },
        { label: 'days', value: 'd' },
        { label: 'weeks', value: 'w' },
        { label: 'months', value: 'M' },
        { label: 'years', value: 'y' },
    ];

    const parseRelative = (raw: string) => {
        const m = raw.match(/^now([+-])(\d+)([smhdwMy])$/);
        if (m) return { sign: m[1] as '+' | '-', amount: m[2], unit: m[3] };
        return { sign: '-' as '+' | '-', amount: '1', unit: 'd' };
    };

    const rel = parseRelative(typeof value === 'string' ? value : '');
    const emit = (sign: string, amount: string, unit: string) =>
        onChange(`now${sign}${amount}${unit}`);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 4 }}>
                {(['absolute', 'relative'] as const).map((m) => (
                    <Button
                        key={m}
                        label={m.charAt(0).toUpperCase() + m.slice(1)}
                        size="small"
                        type="button"
                        severity={mode === m ? undefined : 'secondary'}
                        outlined={mode !== m}
                        onClick={() => {
                            setMode(m);
                            setConversionError(null);
                            if (m === 'relative') emit('-', '1', 'd');
                            else onChange('');
                        }}
                        style={{ flex: 1, fontSize: 11 }}
                    />
                ))}
            </div>

            {mode === 'absolute' ? (
                // Convierte los filtros UTC a la hora seleccionada para editarlos en el calendario.
                <Calendar
                    value={
                        value
                            ? getLocalDateTimeInTimeZone(value, timeZone)
                            : null
                    }
                    onChange={(e) => {
                        if (!(e.value instanceof Date)) return;
                        try {
                            onChange(localDateTimeToUtc(e.value, timeZone));
                            setConversionError(null);
                        } catch (error) {
                            if (error instanceof RangeError) {
                                setConversionError(error.message);
                                return;
                            }
                            throw error;
                        }
                    }}
                    showTime
                    showSeconds
                    dateFormat="yy-mm-dd"
                    style={{ width: '100%' }}
                />
            ) : (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-color-secondary)', whiteSpace: 'nowrap' }}>
                        now
                    </span>
                    <Dropdown
                        value={rel.sign}
                        options={[{ label: '-', value: '-' }]}
                        onChange={(e) => emit(e.value, rel.amount, rel.unit)}
                        style={{ width: 60 }}
                    />
                    <InputText
                        value={rel.amount}
                        onChange={(e) =>
                            emit(rel.sign, e.target.value.replace(/\D/g, '') || '1', rel.unit)
                        }
                        style={{ width: 60, textAlign: 'center' }}
                        keyfilter="int"
                    />
                    <Dropdown
                        value={rel.unit}
                        options={RELATIVE_UNITS}
                        onChange={(e) => emit(rel.sign, rel.amount, e.value)}
                        style={{ flex: 1 }}
                    />
                </div>
            )}
            {conversionError && (
                <small className="p-error">{conversionError}</small>
            )}
        </div>
    );
};

// ─── ValueInput ───────────────────────────────────────────────────────────────

interface ValueInputProps {
    colType: ColumnDtoTypeEnum;
    operator: FilterDtoOperatorEnum;
    value: FilterDto['value'];
    onChange: (v: FilterDto['value']) => void;
}

// Small reusable style for hint/help text under an input
const hintStyle: React.CSSProperties = {
    display: 'block',
    marginTop: 4,
    color: 'var(--text-color-secondary)',
    fontSize: 12,
    lineHeight: 1.4,
};

const ValueInput: React.FC<ValueInputProps> = ({ colType, operator, value, onChange }) => {
    const { t } = useTranslation();
    const { selectedTimezone } = useContext(MarsinetContext);

    if (NO_VALUE_OPERATORS.has(operator)) {
        return (
            <span style={{ color: 'var(--text-color-secondary)', fontStyle: 'italic', fontSize: 13 }}>
                {t('pages.dashboards.valueInput.noValueRequired')}
            </span>
        );
    }

    if (ARRAY_VALUE_OPERATORS.has(operator)) {
        return (
            <div>
                <Chips
                    value={Array.isArray(value) ? (value as string[]) : []}
                    onChange={(e) => onChange(e.value)}
                    style={{ width: '100%' }}
                />
                <small style={hintStyle}>{t('pages.dashboards.valueInput.chipsHint')}</small>
            </div>
        );
    }

    if (RANGE_OPERATORS.has(operator)) {
        const vals = Array.isArray(value) ? value : [null, null];
        const update = (i: number, v: unknown) => {
            const next = [...vals];
            next[i] = v;
            onChange(next as any);
        };

        const renderOne = (i: number) => {
            if (colType === ColumnDtoTypeEnum.Number)
                return (
                    <InputNumber
                        value={vals[i] as number ?? null}
                        onValueChange={(e) => update(i, e.value)}
                        style={{ width: '100%' }}
                    />
                );
            if (colType === ColumnDtoTypeEnum.Timestamp)
                return (
                    <TimestampInput
                        value={vals[i] as string}
                        onChange={(v) => update(i, v)}
                        timeZone={selectedTimezone}
                    />
                );
            return (
                <InputText
                    value={(vals[i] as string) ?? ''}
                    onChange={(e) => update(i, e.target.value)}
                    style={{ width: '100%' }}
                />
            );
        };

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                    <label style={labelStyle}>{t('pages.dashboards.valueInput.from')}</label>
                    {renderOne(0)}
                </div>
                <div>
                    <label style={labelStyle}>{t('pages.dashboards.valueInput.to')}</label>
                    {renderOne(1)}
                </div>
                <small style={hintStyle}>{t('pages.dashboards.valueInput.rangeHint')}</small>
            </div>
        );
    }

    // Single value
    if (colType === ColumnDtoTypeEnum.Number)
        return (
            <InputNumber
                value={(value as number) ?? null}
                onValueChange={(e) => onChange(e.value)}
                style={{ width: '100%' }}
            />
        );

    if (colType === ColumnDtoTypeEnum.Timestamp)
        return (
            <TimestampInput
                value={value as string}
                onChange={onChange as (v: string) => void}
                timeZone={selectedTimezone}
            />
        );

    return (
        <InputText
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            style={{ width: '100%' }}
        />
    );
};

// ─── FilterDialog ─────────────────────────────────────────────────────────────

interface FilterDialogProps {
    visible: boolean;
    initial: FilterDto | null;
    columnOptions: Record<string, ColumnDto>;
    onConfirm: (filter: FilterDto) => void;
    onHide: () => void;
}

const FilterDialog: React.FC<FilterDialogProps> = ({
    visible, initial, columnOptions, onConfirm, onHide,
}) => {
    const firstKey = Object.keys(columnOptions)[0] ?? '';
    const firstCol = columnOptions[firstKey];
    const { t } = useTranslation();

    const [selectedColumn, setSelectedColumn] = useState(null);

    const buildDefault = (): FilterDto => {
        const col = firstCol;
        const colType = col.type;
        return {
            column: col,
            operator: FilterDtoOperatorEnum.Eq,
            value: defaultValueForOperator(FilterDtoOperatorEnum.Eq, colType),
        };
    };

    const [draft, setDraft] = useState<FilterDto>(() =>
        initial ? { ...initial } : buildDefault()
    );

    React.useEffect(() => {
        if (visible) setDraft(initial ? { ...initial } : buildDefault());
    }, [visible, initial]);

    const colType = draft.column.type;

    const columnDropdownOptions = Object.keys(columnOptions).map((key) => ({
        label: key,
        value: key,
    }));

    const operatorOptions = getAvailableFilterOperators(colType).map((op) => ({
        label: OPERATOR_SHORT[op] ?? op,
        value: op,
    }));

    const updateColumn = (key: string) => {
        const newCol = columnOptions[key];
        const newType = newCol.type;
        const supportedOps = getAvailableFilterOperators(newType);
        const newOp = supportedOps.includes(draft.operator) ? draft.operator : supportedOps[0];
        setDraft({ column: newCol, operator: newOp, value: defaultValueForOperator(newOp, newType) });
    };

    const updateOperator = (op: FilterDtoOperatorEnum) =>
        setDraft((d) => ({ ...d, operator: op, value: defaultValueForOperator(op, colType) }));

    const isValid = (() => {
        if (!draft.column) return false;
        if (NO_VALUE_OPERATORS.has(draft.operator)) return true;
        if (ARRAY_VALUE_OPERATORS.has(draft.operator))
            return Array.isArray(draft.value) && (draft.value as any[]).length > 0;
        if (RANGE_OPERATORS.has(draft.operator)) {
            const arr = Array.isArray(draft.value) ? draft.value : [];
            return arr[0] != null && arr[0] !== '' && arr[1] != null && arr[1] !== '';
        }
        return draft.value != null && draft.value !== '';
    })();

    const footer = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
                label={t('pages.dashboards.delete.confirm_reject')}
                severity="secondary"
                outlined
                size="small"
                type="button"
                onClick={onHide}
            />
            <Button
                label={initial ? 'Update filter' : t('pages.dashboards.datasource.add_filter')}
                icon="pi pi-check"
                size="small"
                type="button"
                disabled={!isValid}
                onClick={() => onConfirm(draft)}
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={
                <span style={{ fontSize: 14, fontWeight: 600 }}>
                    {initial ? t('pages.dashboards.datasource.edit_filter') : t('pages.dashboards.datasource.add_filter')}
                </span>
            }
            footer={footer}
            style={{ width: 480 }}
            modal
            draggable={false}
            resizable={false}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 4 }}>

                {/* Column */}
                <div>
                    <label style={labelStyle}>{t('pages.dashboards.datasource.column')}</label>
                    <Dropdown
                        value={selectedColumn}
                        options={columnDropdownOptions}
                        onChange={(e) => {
                            setSelectedColumn(e.value);
                            updateColumn(e.value);
                        }}
                        style={{ width: '100%' }}
                        filter={columnDropdownOptions.length > 8}
                    />
                </div>

                {/* Operator */}
                <div>
                    <label style={labelStyle}>{t('pages.dashboards.datasource.operator')}</label>
                    <Dropdown
                        value={draft.operator}
                        options={operatorOptions}
                        onChange={(e) => updateOperator(e.value)}
                        style={{ width: '100%' }}
                    />
                </div>

                {/* Value */}
                {!NO_VALUE_OPERATORS.has(draft.operator) && (
                    <div>
                        <label style={labelStyle}>{t('pages.dashboards.datasource.value')}</label>
                        <ValueInput
                            colType={colType}
                            operator={draft.operator}
                            value={draft.value}
                            onChange={(v) => setDraft((d) => ({ ...d, value: v }))}
                        />
                    </div>
                )}
            </div>
        </Dialog>
    );
};

// ─── StepFilters ──────────────────────────────────────────────────────────────

export const StepFilters: React.FC<StepFiltersProps> = ({
    formik, filtersPath, columnOptions,
}) => {
    const { t } = useTranslation();

    const [dialogVisible, setDialogVisible] = useState(false);
    const [editIndex, setEditIndex] = useState<number | null>(null);

    const resolvedFilters = filtersPath
        .replace('dataSource.', '')
        .split('.')
        .reduce((obj: any, key) => obj?.[key], formik.values.dataSource);

    const conditions: FilterDto[] = resolvedFilters?.conditions ?? [];
    const operator: FiltersDtoOperatorEnum =
        resolvedFilters?.operator ?? FiltersDtoOperatorEnum.And;

    const setFilters = useCallback(
        (newConditions: FilterDto[], newOperator?: FiltersDtoOperatorEnum) => {
            formik.setFieldValue(
                filtersPath,
                newConditions.length > 0
                    ? { conditions: newConditions, operator: newOperator ?? operator }
                    : undefined
            );
        },
        [filtersPath, formik, operator]
    );

    const openAdd = () => { setEditIndex(null); setDialogVisible(true); };
    const openEdit = (i: number) => { setEditIndex(i); setDialogVisible(true); };

    const handleConfirm = (filter: FilterDto) => {
        if (editIndex === null) {
            setFilters([...conditions, filter]);
        } else {
            setFilters(conditions.map((c, i) => (i === editIndex ? filter : c)));
        }
        setDialogVisible(false);
    };

    const handleRemove = (i: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setFilters(conditions.filter((_, idx) => idx !== i));
    };


    return (
        <div className="field col-12">
            {/* ── AND / OR ── */}
            {conditions.length > 1 && (
                <>
                    <label
                        htmlFor="input-create-datasource-tables"
                        className="font-medium"
                    >
                        {t('pages.dashboards.datasource.select_logical_operator')}
                    </label>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            marginBottom: 12,
                        }}
                    >
                        <div className="flex gap-2">
                            {Object.values(FiltersDtoOperatorEnum).map((op) => {
                                const selected = operator === op;
                                return (
                                    <div
                                        key={op}
                                        className={`
                            flex align-items-center gap-2 px-3 py-2
                            border-round-lg cursor-pointer border-2
                            transition-all transition-duration-150
                            ${selected
                                                ? 'border-primary bg-primary-50'
                                                : 'border-200 surface-card hover:border-primary-300'
                                            }
                        `}
                                        onClick={() => formik.setFieldValue(`${filtersPath}.operator`, op)}
                                    >
                                        <span className={`font-medium text-sm ${selected ? 'text-primary' : 'text-700'}`}>
                                            {op.toUpperCase()}
                                        </span>
                                        {selected && (
                                            <i className="pi pi-check text-xs text-primary" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}


            <label
                htmlFor="input-create-datasource-tables"
                className="font-medium"
            >
                {t('pages.dashboards.datasource.select_filters')}
            </label>

            {/* ── Filter table ── */}
            {conditions.length > 0 && (
                <div style={{ marginBottom: 10, border: '1px solid var(--surface-200)', borderRadius: 6, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: 'var(--surface-50)', borderBottom: '1px solid var(--surface-200)' }}>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-color-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
                                    Column
                                </th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-color-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
                                    Operator
                                </th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-color-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
                                    Value
                                </th>
                                <th style={{ width: 60 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {conditions.map((cond, i) => {
                                const isLast = i === conditions.length - 1;
                                const valueDisplay = (() => {
                                    if (NO_VALUE_OPERATORS.has(cond.operator)) return <em style={{ color: 'var(--text-color-secondary)' }}>—</em>;
                                    if (ARRAY_VALUE_OPERATORS.has(cond.operator)) {
                                        const arr = Array.isArray(cond.value) ? cond.value : [];
                                        return arr.slice(0, 3).join(', ') + (arr.length > 3 ? ', …' : '');
                                    }
                                    if (RANGE_OPERATORS.has(cond.operator)) {
                                        const arr = Array.isArray(cond.value) ? cond.value : ['', ''];
                                        return `${arr[0]} – ${arr[1]}`;
                                    }
                                    return String(cond.value ?? '');
                                })();

                                return (
                                    <tr
                                        key={i}
                                        onClick={() => openEdit(i)}
                                        title="Click to edit"
                                        style={{
                                            borderBottom: isLast ? 'none' : '1px solid var(--surface-100)',
                                            cursor: 'pointer',
                                            transition: 'background 0.12s',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-50)')}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                                    >
                                        <td style={{ padding: '7px 10px', fontWeight: 500, color: 'var(--text-color)' }}>
                                            <i className="pi pi-filter" style={{ fontSize: 10, opacity: 0.45, marginRight: 6 }} />
                                            {cond.column?.column ?? '?'}
                                        </td>
                                        <td style={{ padding: '7px 10px', color: 'var(--primary-600, #2563eb)', fontWeight: 600 }}>
                                            {OPERATOR_SHORT[cond.operator] ?? cond.operator}
                                        </td>
                                        <td style={{ padding: '7px 10px', color: 'var(--text-color-secondary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {valueDisplay}
                                        </td>
                                        <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                                            <i
                                                className="pi pi-trash"
                                                onClick={(e) => handleRemove(i, e)}
                                                title="Remove"
                                                style={{ fontSize: 11, opacity: 0.45, cursor: 'pointer', padding: 4, borderRadius: 4, transition: 'opacity 0.1s' }}
                                                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = '1')}
                                                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = '0.45')}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Add button ── */}
            <Button
                label={t('pages.dashboards.datasource.add_filter')}
                icon="pi pi-plus"
                severity="secondary"
                outlined
                size="small"
                type="button"
                onClick={openAdd}
                disabled={Object.keys(columnOptions).length === 0}
            />

            {/* ── Dialog ── */}
            <FilterDialog
                visible={dialogVisible}
                initial={editIndex !== null ? (conditions[editIndex] ?? null) : null}
                columnOptions={columnOptions}
                onConfirm={handleConfirm}
                onHide={() => setDialogVisible(false)}
            />
        </div>
    );
};